import React, { useEffect, useState } from 'react';
import { useFormik } from 'formik';
import { useTranslation } from 'react-i18next';
import { Dialog } from 'primereact/dialog';

import { ModalAddDataSourceProps } from 'apps/marsinet/src/app/models/datasourceModels';
import { useAddDataSourceValidationSchema } from 'apps/marsinet/src/app/validations/addDataSourceValidationSchema';
import {
    AggregateOperationDto,
    ColumnDto,
    DataSourceDto,
    DataSourceDtoTypeEnum,
} from '../../../../../../typescript-axios';
import { FormField } from '../../../components/form-field-add-dashboard/form-field';
import {
    canSubmit,
    getFormErrorMessage,
    handleNextStep,
    handlePreviousStep,
    isFormFieldInvalid,
    onPageHide,
    Step,
    stepHasChanges,
    stepIsValid
} from '../../utils/form_utils';
import { FormStepFooter } from '../../utils/forms-step-footer';
import { StepSelectTables } from './steps/select_tables';
import { StepFilters } from './steps/filters';
import { StepGroupBy } from './steps/group_by';
import { StepGroupByAggregations } from './steps/group_by_aggregations';
import { StepColumnSelection } from './steps/column_selection';
import { StepOrderBy } from './steps/order_by';
import { getAggregatableColumnOptions, getAvailableBaseColumnsOptions, getAvailableTableOptions, getPostGroupingColumnOptions } from '../../utils/datasource_utils';


const STEPS: Step[] = [
    {
        label: 'pages.dashboards.datasource.tables', // FROM
        fields: ['dataSource.query_definition.table_source'],
        required: true,
    },
    {
        label: 'pages.dashboards.datasource.filters', // WHERE
        fields: ['dataSource.query_definition.filters'],
        required: false,
    },
    {
        label: 'pages.dashboards.datasource.group_by', // GROUP BY
        fields: ['dataSource.query_definition.columns', 'dataSource.query_definition.group_by'],
        required: false,
    },
    {
        label: 'pages.dashboards.datasource.group_by_aggregations', // AGGREGATIONS
        fields: ['dataSource.query_definition.group_by.aggregate_operations'],
        required: false,
        skipIf: (values, initialValues) =>
            !stepHasChanges(['dataSource.query_definition.group_by'], values, initialValues),

    },
    {
        label: 'pages.dashboards.datasource.group_by_filters', // HAVING
        fields: ['dataSource.query_definition.group_by.filters'],
        required: false,
        skipIf: (values, initialValues) =>
            !stepHasChanges(['dataSource.query_definition.group_by'], values, initialValues),
    },
    {
        label: 'pages.dashboards.datasource.column_selection', // SELECT
        fields: ['dataSource.query_definition.columns'],
        required: true,
        skipIf: (values, initialValues) => {
            const groupBy = values?.dataSource?.query_definition?.group_by;
            const columns = values?.dataSource?.query_definition?.columns;
            return !!groupBy && !!columns;
        },
    },
    {
        label: 'pages.dashboards.datasource.order_by', // ORDER BY
        fields: ['dataSource.query_definition.order_by'],
        required: false,
    },

    {
        label: 'pages.dashboards.datasource.name', // Name
        fields: ['dataSource.name'],
        required: true,
    },
];

const INITIAL_VALUES = {
    dataSource: {
        name: '',
        type: DataSourceDtoTypeEnum.Custom,
        query_definition: {
            table_source: [],
        },
    } as DataSourceDto,
}


const ModalAddDataSource: React.FC<ModalAddDataSourceProps> = ({
    visible,
    setVisible,
    createDataSourceFunction,
    baseDataSources,
    baseTablesRelations
}) => {
    const { t } = useTranslation();
    const [activeStep, setActiveStep] = useState(0);
    const [baseTables, setBaseTables] = useState<Record<string, ColumnDto[]>>({});

    const formik = useFormik({
        initialValues: INITIAL_VALUES,
        validationSchema: useAddDataSourceValidationSchema(),
        validateOnMount: false,
        onSubmit: async (values, { resetForm, setFieldError, setFieldTouched, setStatus }) => {
            setStatus(null);
            try {
                await createDataSourceFunction(values);

                resetForm();
                setActiveStep(0);
                setVisible(false);
            } catch (error) {
                if (error instanceof Error && error.message.includes('already exists')) {
                    setFieldError('dataSource.name', error.message);
                    setFieldTouched('dataSource.name', true, false);
                }
                else if (error instanceof Error && error.message.includes('Data source is invalid')) {
                    setStatus({ submitError: error.message });
                }
                else {
                    console.error('Something is wrong with the Data Source:', error);
                }
            }
        },
    });


    useEffect(() => {
        const mappedBaseTables = baseDataSources.reduce<Record<string, ColumnDto[]>>(
            (acc, dataSource) => {
                const tableName = dataSource.query_definition.table_source?.[0];
                if (!tableName) return acc;
                acc[tableName] = dataSource.query_definition.columns ?? [];
                return acc;
            },
            {}
        );

        setBaseTables(mappedBaseTables);
    }, [baseDataSources]);

    const isCurrentStepRequired = STEPS[activeStep].required;

    // Next is disabled only on required steps where fields aren't valid yet
    const isNextDisabled = isCurrentStepRequired
        ? !stepHasChanges(STEPS[activeStep].fields, formik.values, formik.initialValues) || !stepIsValid(STEPS[activeStep].fields, formik.errors)
        : false;

    // Show "Skip" label on optional steps with no changes
    const isSkipping = !isCurrentStepRequired && !stepHasChanges(STEPS[activeStep].fields, formik.values, formik.initialValues);

    const isLastStep = activeStep === STEPS.length - 1;

    // ── Footer ───────────────────────────────────────────────────────────────

    const data_source_form_name: string = "datasource-form";

    const footer = <FormStepFooter
        activeStep={activeStep}
        isLastStep={isLastStep}
        isSkipping={isSkipping}
        isNextDisabled={isNextDisabled}
        canSubmit={canSubmit(formik.initialValues, formik.values, formik.isValid)}
        onPrevious={() => handlePreviousStep(activeStep, STEPS, setActiveStep, formik)}
        onNext={() => handleNextStep(activeStep, STEPS, isSkipping, setActiveStep, formik)}
        labels={{
            back: t('pages.dashboards.back'),
            next: t('pages.dashboards.next'),
            skip: t('pages.dashboards.skip'),
            create: t('pages.dashboards.datasource.createDataSource'),
        }}
        form_name={data_source_form_name}
    />;

    return (
        <Dialog
            header={t('pages.dashboards.datasource.create')}
            visible={visible}
            className="mx-2 md:w-6 lg:w-4"
            onHide={() => onPageHide(formik, setActiveStep, setVisible)}
            breakpoints={{ '960px': '75vw', '641px': '100vw' }}
            blockScroll={true}
            footer={footer}
        >
            {/* Step label */}
            <div className="mb-3 text-lg font-semibold text-900">
                {t(STEPS[activeStep].label)}
            </div>

            {/* Step progress dots */}
            <div className="flex gap-2 mb-4">
                {STEPS.map((_, i) => (
                    <span
                        key={i}
                        className={`border-round-xl transition-all transition-duration-150 ${i === activeStep
                            ? 'bg-primary'
                            : i < activeStep
                                ? 'bg-primary-200'
                                : 'bg-300'
                            }`}
                        style={{ height: '6px', flex: 1 }}
                    />
                ))}
            </div>

            <form id={data_source_form_name} autoComplete="off" onSubmit={formik.handleSubmit}>
                <div className="grid formgrid p-fluid">
                    {/* ── Step Select Tables  ── */}
                    {STEPS[activeStep].label === 'pages.dashboards.datasource.tables' && (
                        <StepSelectTables
                            formik={formik}
                            tableOptions={getAvailableTableOptions(baseTables, baseTablesRelations, formik.values.dataSource.query_definition.table_source ?? [])}
                        />
                    )}

                    {/* ── Step Where ── */}
                    {STEPS[activeStep].label === 'pages.dashboards.datasource.filters' && (
                        <StepFilters
                            formik={formik}
                            filtersPath="dataSource.query_definition.filters"
                            columnOptions={getAvailableBaseColumnsOptions(baseTables, formik.values.dataSource.query_definition.table_source)}
                        />
                    )}

                    {/* ── Step Select Group by ── */}
                    {STEPS[activeStep].label === 'pages.dashboards.datasource.group_by' && (
                        <StepGroupBy
                            formik={formik}
                            columnOptions={getAvailableBaseColumnsOptions(baseTables, formik.values.dataSource.query_definition.table_source)}
                        />
                    )}

                    {/* ── Step Group by - Aggregations ── */}
                    {STEPS[activeStep].label === 'pages.dashboards.datasource.group_by_aggregations' && (
                        <StepGroupByAggregations
                            formik={formik}
                            aggregatableOptions={getAggregatableColumnOptions(baseTables, formik.values.dataSource.query_definition.table_source, formik.values.dataSource.query_definition.columns)}
                        />
                    )}

                    {/* ── Step Group by - Filters ── */}
                    {STEPS[activeStep].label === 'pages.dashboards.datasource.group_by_filters' && (
                        <StepFilters
                            formik={formik}
                            filtersPath="dataSource.query_definition.group_by.filters"
                            columnOptions={getPostGroupingColumnOptions(
                                baseTables,
                                formik.values.dataSource.query_definition.table_source,
                                formik.values.dataSource.query_definition.columns,
                                formik.values.dataSource.query_definition.group_by
                            )}
                        />
                    )}

                    {/* ── Step Column Selection ── */}
                    {STEPS[activeStep].label === 'pages.dashboards.datasource.column_selection' && (
                        <StepColumnSelection
                            formik={formik}
                            columnOptions={getAvailableBaseColumnsOptions(baseTables, formik.values.dataSource.query_definition.table_source)} />
                    )}

                    {/* ── Step Select Order by ── */}
                    {STEPS[activeStep].label === 'pages.dashboards.datasource.order_by' && (
                        <StepOrderBy
                            formik={formik}
                            columnOptions={getPostGroupingColumnOptions(
                                baseTables,
                                formik.values.dataSource.query_definition.table_source,
                                formik.values.dataSource.query_definition.columns,
                                formik.values.dataSource.query_definition.group_by
                            )} />
                    )}

                    {/* ── Step Choose Datasource name ── */}
                    {STEPS[activeStep].label === 'pages.dashboards.datasource.name' && (
                        <FormField
                            id="input-create-datasource-name"
                            label={t('pages.dashboards.datasource.select_name')}
                            name="name"
                            value={formik.values.dataSource.name}
                            placeholder={undefined}
                            onBlur={formik.handleBlur}
                            onChange={(e) => {
                                formik.setFieldValue('dataSource.name', e.target.value);
                                formik.setFieldTouched('dataSource.name', true, false);
                            }}
                            isInvalid={isFormFieldInvalid('dataSource.name', formik.touched, formik.errors)}
                            errorMessage={getFormErrorMessage('dataSource.name', formik.touched, formik.errors)}
                            className="col-12"
                            height="40px"
                        />
                    )}

                    {formik.status?.submitError && (
                        <div className="flex justify-content-center align-items-center text-red-500">
                            {formik.status.submitError}
                        </div>
                    )}
                </div>
            </form>
        </Dialog>
    );
};

export default ModalAddDataSource;